import React, { useState } from 'react';
import { Box, Typography, Button, useTheme } from '@mui/material';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import cardData from './data/articles';

export default function MotionCards() {
  const theme = useTheme();

  const [expanded, setExpanded] = useState({});

  // Only include these categories
  const allowedCategories = ['CTMS', 'Insights RM', 'Informer'];

  const categories = [...new Set(cardData
    .map((c) => c.category)
    .filter((cat) => allowedCategories.includes(cat))
  )];

  const toggleExpand = (category) => {
    setExpanded((prev) => ({ ...prev, [category]: !prev[category] }));
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 6, p: 4 }}>
      {categories.map((category) => {
        const articles = cardData
          .filter((c) => c.category === category)
          .filter((c) => allowedCategories.includes(c.category)); // extra safety
        const isExpanded = expanded[category];
        const visibleArticles = isExpanded ? articles : articles.slice(0, 2);

        return (
          <Box key={category}>
            {/* Section Heading */}
            <Typography
              variant="h4"
              sx={{
                mb: 4,
                fontWeight: 'bold',
                justifyContent: 'center',
                display: 'flex',
                color: theme.palette.text.primary,
              }}
            >
              {category}
            </Typography>

            {/* Articles */}
            {visibleArticles.map((card, index) => {
              const isEven = index % 2 === 0;

              return (
                <motion.div
                  key={card.id}
                  initial={{ opacity: 0, x: isEven ? 100 : -100 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.8 }}
                  viewport={{ once: false, amount: 0.3 }}
                >
                  {/* Card Layout */}
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: {
                        xs: 'column',
                        sm: 'column',
                        md: card.img ? (isEven ? 'row' : 'row-reverse') : 'column',
                      },
                      alignItems: 'center',
                      gap: 3,
                      borderRadius: 3,
                      p: 3,
                      mb: 2,
                      boxShadow: '0px 4px 10px rgba(0,0,0,0.1)',
                      minHeight: 200,
                    }}
                  >
                    {/* Image (only if exists) */}
                    {card.img && (
                      <Box
                        component="img"
                        src={card.img}
                        alt={card.title}
                        sx={{
                          width: { xs: '100%', sm: '100%', md: 400 },
                          height: { xs: 180, sm: 200, md: 200 },
                          borderRadius: 2,
                          objectFit: 'cover',
                          flexShrink: 0,
                        }}
                      />
                    )}

                    {/* Text */}
                    <Box>
                      {/* Title: Uses router Link for navigation to details page */}
                      <Typography
                        component={Link}
                        to={`/article/${card.id}`}
                        sx={{
                          display: 'block',
                          fontSize: '1.5rem',
                          fontWeight: 'bold',
                          color: card.bgColor || '#1976d2',
                          mb: 1,
                          textDecoration: 'none',
                          '&:hover': {
                            textDecoration: 'underline',
                            textDecorationColor: card.bgColor || '#1976d2',
                          },
                        }}
                      >
                        {card.title}
                      </Typography>
                      <Typography variant="body1" color={theme.palette.text.primary}>
                        {card.text} {/* Snippet for list view */}
                      </Typography>
                    </Box>
                  </Box>
                </motion.div>
              );
            })}

            {/* See More button */}
            {articles.length > 2 && (
              <Button
                variant="contained"
                onClick={() => toggleExpand(category)}
                sx={{
                  mt: 1,
                  backgroundColor: '#00AEEF',
                  color: 'white',
                  px: 4,
                  py: 1.2,
                  borderRadius: '20px',
                  textTransform: 'none',
                  fontWeight: 500,
                  '&:hover': { backgroundColor: '#008ECC' },
                  justifyItems: 'center',
                  display: 'flex',
                  marginLeft: 'auto',
                }}
              >
                {isExpanded ? 'See Less' : 'See More'}
              </Button>
            )}
          </Box>
        );
      })}
    </Box>
  );
}
